import { useMemo, useState } from 'react'
import { ArrowDown, ArrowUp, Plus, Search, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Badge as UiBadge } from '@/components/ui/badge'
import { useAdminBadges } from '@/services/admin/badges.service'
import { BadgeMedallion } from '@/components/super-admin/badges/BadgeMedallion'
import type { Badge as DomainBadge, Tier } from '@/components/super-admin/badges/types'

/**
 * Picker de badges pra aparecer no certificado da empresa.
 *
 * Carrega:
 * - **Globais** (`scope: 'global'`) — sempre visíveis pra qualquer empresa.
 * - **Da empresa** (`companyId`) — quando `companyId` está definido (modo
 *   "editar"). No modo "criar" (companyId=null), só globais ficam disponíveis
 *   porque a empresa ainda não tem id pra ter badges próprias.
 *
 * O resultado é deduplicado por id. Mostra:
 * - **Chips** das badges selecionadas (ordem = `value` recebido), com
 *   botões `↑`/`↓` (reordenar) e `×` (remover).
 * - **Botão "Adicionar badges"** que abre um Popover com search + lista
 *   de checkbox (toda badge disponível, marcada se já está em `value`).
 *
 * Por que 2 fetches: o endpoint `GET /admin/badges` aceita só um filtro de
 * `companyId` (ou `scope: 'global'`). Em vez de criar um endpoint dedicado,
 * fazemos 2 calls e combinamos no client — volume é trivial (catálogo < 100).
 *
 * Multi-tenancy: o back valida no PATCH/POST que cada `badgeId` enviado é
 * global OU da empresa-alvo (400 BADGE_NOT_AVAILABLE caso contrário) — o
 * picker é só UX, o gate de segurança fica no service.
 */

export interface CertificateBadgePickerProps {
  /** Id da empresa sendo editada. `null` em modo "criar". */
  companyId: string | null
  /** Lista ordenada de badgeIds selecionados. Ordem importa pra render. */
  value: string[]
  /** Chamado com a nova lista quando o usuário muda algo. */
  onChange: (badgeIds: string[]) => void
  disabled?: boolean
}

export function CertificateBadgePicker({
  companyId,
  value,
  onChange,
  disabled = false,
}: CertificateBadgePickerProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')

  // Globais sempre. Per-company só quando temos companyId (edição).
  const globalsQuery = useAdminBadges({ scope: 'global' })
  const companyQuery = useAdminBadges(companyId ? { companyId } : undefined)
  const companyEnabled = !!companyId

  const isLoading = globalsQuery.isLoading || (companyEnabled && companyQuery.isLoading)

  // Combina + dedupe por id. Globais primeiro pra desempate previsível.
  const allAvailable = useMemo<DomainBadge[]>(() => {
    const map = new Map<string, DomainBadge>()
    for (const b of globalsQuery.data ?? []) map.set(b.id, b)
    if (companyEnabled) {
      for (const b of companyQuery.data ?? []) map.set(b.id, b)
    }
    return Array.from(map.values())
  }, [globalsQuery.data, companyQuery.data, companyEnabled])

  const byId = useMemo(() => {
    const m = new Map<string, DomainBadge>()
    for (const b of allAvailable) m.set(b.id, b)
    return m
  }, [allAvailable])

  // Lista filtrada pelo search dentro do popover.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return allAvailable
    return allAvailable.filter(
      (b) =>
        b.name.toLowerCase().includes(q) ||
        (b.howToEarn ? b.howToEarn.toLowerCase().includes(q) : false)
    )
  }, [allAvailable, search])

  const selectedSet = useMemo(() => new Set(value), [value])

  const toggle = (id: string) => {
    if (selectedSet.has(id)) {
      onChange(value.filter((x) => x !== id))
    } else {
      onChange([...value, id])
    }
  }

  const remove = (id: string) => onChange(value.filter((x) => x !== id))

  const move = (id: string, direction: -1 | 1) => {
    const idx = value.indexOf(id)
    if (idx === -1) return
    const next = direction === -1 ? idx - 1 : idx + 1
    if (next < 0 || next >= value.length) return
    const out = value.slice()
    ;[out[idx], out[next]] = [out[next], out[idx]]
    onChange(out)
  }

  return (
    <div className="space-y-3">
      {/* Lista de selecionadas (chips) — ordem segue `value` */}
      {value.length === 0 ? (
        <p data-testid="badge-picker-empty" className="text-xs text-muted-foreground italic">
          Nenhuma badge selecionada — usuários verão o certificado sem a faixa de conquistas.
        </p>
      ) : (
        <div className="flex flex-col gap-2" data-testid="badge-picker-chips">
          {value.map((id, idx) => {
            const b = byId.get(id)
            const isFirst = idx === 0
            const isLast = idx === value.length - 1
            return (
              <div
                key={id}
                data-testid={`badge-chip-${id}`}
                className="flex items-center gap-2 rounded-md border border-border/40 bg-card/40 px-2.5 py-1.5"
              >
                {b ? (
                  <>
                    <BadgeMedallion
                      tier={b.tier as Tier}
                      iconUrl={b.iconUrl ?? null}
                      size={28}
                      showInscription={false}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{b.name}</p>
                      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
                        {b.tier}
                      </p>
                    </div>
                  </>
                ) : (
                  // Badge no `value` mas não no catálogo (foi removida do back?)
                  // Mostra o id pra super_admin saber e poder remover manual.
                  <div className="flex-1 text-xs text-amber-500 italic truncate">
                    Badge não disponível (id: {id})
                  </div>
                )}

                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  aria-label={`Mover ${b?.name ?? 'badge'} pra cima`}
                  disabled={disabled || isFirst}
                  onClick={() => move(id, -1)}
                >
                  <ArrowUp className="h-3.5 w-3.5" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  aria-label={`Mover ${b?.name ?? 'badge'} pra baixo`}
                  disabled={disabled || isLast}
                  onClick={() => move(id, 1)}
                >
                  <ArrowDown className="h-3.5 w-3.5" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-destructive hover:text-destructive"
                  aria-label={`Remover ${b?.name ?? 'badge'}`}
                  disabled={disabled}
                  onClick={() => remove(id)}
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            )
          })}
        </div>
      )}

      {/* Botão + Popover de seleção */}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled}
            data-testid="badge-picker-trigger"
          >
            <Plus className="h-4 w-4 mr-1.5" />
            Adicionar badges
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[360px] p-0" align="start">
          <div className="p-2 border-b border-border/40">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                placeholder="Buscar por nome..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-8 pl-7 text-sm"
                data-testid="badge-picker-search"
              />
            </div>
          </div>
          <ScrollArea className="h-[260px]">
            {isLoading ? (
              <p className="text-xs text-muted-foreground text-center py-6">Carregando…</p>
            ) : filtered.length === 0 ? (
              <p
                data-testid="badge-picker-no-results"
                className="text-xs text-muted-foreground text-center py-6"
              >
                {search ? 'Nenhuma badge encontrada.' : 'Nenhuma badge disponível.'}
              </p>
            ) : (
              <ul className="py-1">
                {filtered.map((b) => {
                  const checked = selectedSet.has(b.id)
                  return (
                    <li key={b.id}>
                      <label
                        data-testid={`badge-option-${b.id}`}
                        className="flex items-center gap-2.5 px-3 py-2 hover:bg-muted/50 cursor-pointer"
                      >
                        <Checkbox
                          checked={checked}
                          onCheckedChange={() => toggle(b.id)}
                          disabled={disabled}
                        />
                        <BadgeMedallion
                          tier={b.tier as Tier}
                          iconUrl={b.iconUrl ?? null}
                          size={28}
                          showInscription={false}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{b.name}</p>
                          <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
                            {b.tier}
                          </p>
                        </div>
                        {b.scope.type === 'global' && (
                          <UiBadge variant="secondary" className="text-[9px] px-1.5 py-0">
                            global
                          </UiBadge>
                        )}
                      </label>
                    </li>
                  )
                })}
              </ul>
            )}
          </ScrollArea>
        </PopoverContent>
      </Popover>
    </div>
  )
}
